import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UserCheck, UserX, Loader2, Clock, Users, Mail, UserPlus, Copy, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useBranches } from '@/hooks/api/branches';
import { useProfiles } from '@/hooks/api/profiles';
import { useMerchantId } from '@/hooks/useMerchantId';
import type { components } from '@/api/schema';

type ProfileRow = components['schemas']['UserResource'] & { full_name: string; role: string };

export default function ApprovalTab() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { merchantId } = useMerchantId();
  const queryClient = useQueryClient();
  const { data: branches = [] } = useBranches();
  const { data: users = [], isLoading: loading } = useProfiles();

  const pendingUsers = users.filter(u => u.status === 'pending');
  const allUsers = users.filter(u => u.status !== 'pending');

  const [selectedRole, setSelectedRole] = useState<'manager' | 'cashier' | 'viewer'>('cashier');
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ProfileRow | null>(null);

  // Invite state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteIdentifier, setInviteIdentifier] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteRole, setInviteRole] = useState<'manager' | 'cashier' | 'viewer'>('cashier');
  const [inviteBranch, setInviteBranch] = useState<string>('all');
  // FR-96: the temporary password is shown once, right after the invite.
  const [inviteResult, setInviteResult] = useState<{ identifier: string; tempPassword: string } | null>(null);

  const invalidateProfiles = () => queryClient.invalidateQueries({ queryKey: ['profiles', merchantId] });

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUser) return;
      const branchId = selectedBranch !== 'all' ? Number(selectedBranch) : null;

      if (selectedRole !== selectedUser.role) {
        const { error } = await apiClient.PATCH('/staff/{user}/role', {
          params: { path: { user: selectedUser.id } },
          body: { role: selectedRole },
        });
        if (error) throw new Error('message' in error ? String(error.message) : t('cust_common.error'));
      }
      if (branchId !== (selectedUser.branch_id ?? null)) {
        const { error } = await apiClient.PATCH('/staff/{user}', {
          params: { path: { user: selectedUser.id } },
          body: { branch_id: branchId },
        });
        if (error) throw new Error('message' in error ? String(error.message) : t('cust_common.error'));
      }
      const { error } = await apiClient.POST('/staff/{user}/approve', {
        params: { path: { user: selectedUser.id } },
      });
      if (error) throw new Error('message' in error ? String(error.message) : t('cust_common.error'));
    },
    onSuccess: () => {
      const branchName = branches.find(b => String(b.id) === selectedBranch)?.name;
      toast({
        title: t('cust_uappr.approved_toast'),
        description: `${selectedUser?.full_name} - ${selectedRole}${branchName ? ` • ${branchName}` : ''}`,
      });
      invalidateProfiles();
      setShowApproveDialog(false);
      setSelectedUser(null);
      setSelectedBranch('all');
    },
    onError: (err: Error) => toast({ title: t('cust_common.error'), description: err.message, variant: 'destructive' }),
  });

  const rejectMutation = useMutation({
    mutationFn: async (pendingUser: ProfileRow) => {
      const { error } = await apiClient.POST('/staff/{user}/reject', {
        params: { path: { user: pendingUser.id } },
      });
      if (error) throw new Error('message' in error ? String(error.message) : t('cust_common.error'));
    },
    onSuccess: (_data, pendingUser) => {
      toast({ title: t('cust_uappr.rejected_toast'), description: pendingUser.full_name });
      invalidateProfiles();
    },
    onError: (err: Error) => toast({ title: t('cust_common.error'), description: err.message, variant: 'destructive' }),
  });

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST('/staff/invite', {
        body: {
          identifier: inviteIdentifier.trim(),
          full_name: inviteFullName.trim(),
          role: inviteRole,
          branch_id: inviteBranch !== 'all' ? Number(inviteBranch) : undefined,
        },
      });
      if (error) throw new Error('message' in error ? String(error.message) : t('cust_common.error'));
      return data.temp_password;
    },
    onSuccess: (tempPassword) => {
      toast({ title: t('staff_invite.toast_title'), description: inviteFullName });
      invalidateProfiles();
      setInviteResult({ identifier: inviteIdentifier.trim(), tempPassword });
      setInviteIdentifier('');
      setInviteFullName('');
      setInviteRole('cashier');
      setInviteBranch('all');
    },
    onError: (err: Error) => toast({ title: t('cust_common.error'), description: err.message, variant: 'destructive' }),
  });

  const closeInviteDialog = (open: boolean) => {
    setShowInviteDialog(open);
    if (!open) setInviteResult(null);
  };

  const roleLabel: Record<string, string> = {
    owner: t('cust_common.role_owner'), manager: t('cust_common.role_manager'), cashier: t('cust_common.role_cashier'), viewer: t('cust_common.role_viewer'),
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active': return <Badge className="text-xs border-0 bg-success/10 text-success">{t('cust_common.status_active')}</Badge>;
      case 'blocked': return <Badge className="text-xs border-0 bg-destructive/10 text-destructive">{t('cust_common.status_blocked')}</Badge>;
      case 'pending': return <Badge className="text-xs border-0 bg-warning/10 text-warning">{t('cust_common.status_pending')}</Badge>;
      default: return <Badge variant="secondary" className="text-xs">{status}</Badge>;
    }
  };

  return (
    <div>
      {/* Invite button */}
      <div className="flex items-center justify-between mb-6">
        <div />
        <Button size="sm" onClick={() => setShowInviteDialog(true)}>
          <UserPlus size={14} className="mr-1.5" />
          {t('cust_appr.invite_staff')}
        </Button>
      </div>

      {/* Pending users */}
      {pendingUsers.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} className="text-warning" />
            <h2 className="text-sm font-semibold text-foreground">{t('cust_uappr.pending_heading', { n: pendingUsers.length })}</h2>
          </div>
          <div className="space-y-2">
            {pendingUsers.map(pu => (
              <div key={pu.id} className="flex items-center justify-between p-4 rounded-xl bg-warning/5 border border-warning/20">
                <div>
                  <p className="text-sm font-medium text-foreground">{pu.full_name || t('cust_uappr.no_name')}</p>
                  <p className="text-xs text-muted-foreground">{pu.phone} • {pu.created_at ? new Date(pu.created_at).toLocaleDateString(i18n.language === 'uz' ? 'uz' : i18n.language === 'ru' ? 'ru-RU' : 'en-US') : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedUser(pu);
                      setSelectedRole((pu.role as 'manager' | 'cashier' | 'viewer') || 'cashier');
                      setSelectedBranch(pu.branch_id != null ? String(pu.branch_id) : 'all');
                      setShowApproveDialog(true);
                    }}
                    disabled={approveMutation.isPending}
                  >
                    {approveMutation.isPending && selectedUser?.id === pu.id ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={14} className="mr-1" />}
                    {t('cust_common.approve')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => rejectMutation.mutate(pu)} disabled={rejectMutation.isPending}>
                    <UserX size={14} className="mr-1" />
                    {t('cust_uappr.reject')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendingUsers.length === 0 && !loading && (
        <div className="mb-8 p-6 rounded-xl border border-border bg-muted/30 text-center">
          <UserCheck size={24} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('cust_appr.no_pending')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('cust_appr.no_pending_hint')}</p>
        </div>
      )}

      {/* All users table */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Users size={16} className="text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_uappr.all_users')}</h2>
        </div>
        {loading ? (
          <div className="flex justify-center p-8"><Loader2 size={24} className="animate-spin text-muted-foreground" /></div>
        ) : allUsers.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 text-center">{t('cust_uappr.no_users')}</p>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_uappr.col_name')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_uappr.col_phone')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_common.role')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_users.col_branch')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_uappr.col_state')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_uappr.col_date')}</th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map(u => {
                  const branch = branches.find(b => b.id === u.branch_id);
                  return (
                    <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="p-3 text-sm text-foreground font-medium">{u.full_name || '—'}</td>
                      <td className="p-3 text-sm text-muted-foreground font-mono">{u.phone || '—'}</td>
                      <td className="p-3"><Badge variant="secondary" className="text-xs">{roleLabel[u.role] || u.role}</Badge></td>
                      <td className="p-3 text-sm text-muted-foreground">{branch?.name ?? t('cust_common.all')}</td>
                      <td className="p-3">{statusBadge(u.status)}</td>
                      <td className="p-3 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString(i18n.language === 'uz' ? 'uz' : i18n.language === 'ru' ? 'ru-RU' : 'en-US') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Approve Dialog with Branch */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('cust_uappr.dialog_title')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-sm font-medium text-foreground">{selectedUser?.full_name || t('cust_uappr.no_name')}</p>
              <p className="text-xs text-muted-foreground">{selectedUser?.phone}</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('cust_uappr.choose_role')}</label>
              <Select value={selectedRole} onValueChange={v => setSelectedRole(v as 'manager' | 'cashier' | 'viewer')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">{t('cust_common.role_manager')}</SelectItem>
                  <SelectItem value="cashier">{t('cust_uappr.role_cashier_seller')}</SelectItem>
                  <SelectItem value="viewer">{t('cust_common.role_viewer')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('cust_appr.assign_branch')}</label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger><SelectValue placeholder={t('cust_common.all_branches')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('cust_common.all_branches')}</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending}>
              {approveMutation.isPending ? <Loader2 size={14} className="animate-spin mr-2" /> : <UserCheck size={14} className="mr-2" />}
              {t('cust_uappr.approve_assign')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={closeInviteDialog}>
        <DialogContent>
          {inviteResult ? (
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <CheckCircle size={32} className="mx-auto text-success" />
                <p className="text-base font-semibold text-foreground">{t('staff_invite.success_title')}</p>
                <p className="text-sm text-muted-foreground">{t('staff_invite.success_hint')}</p>
              </div>
              <CredentialRow label={t('staff_invite.login_label')} value={inviteResult.identifier} />
              <CredentialRow label={t('staff_invite.temp_password_label')} value={inviteResult.tempPassword} />
              <p className="text-xs text-warning">{t('staff_invite.must_change_note')}</p>
              <Button className="w-full" onClick={() => closeInviteDialog(false)}>{t('staff_invite.done')}</Button>
            </div>
          ) : (
          <>
          <DialogHeader><DialogTitle>{t('cust_appr.invite_staff')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('cust_users.full_name')}</label>
              <Input
                placeholder={t('cust_users.full_name_ph')}
                value={inviteFullName}
                onChange={e => setInviteFullName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('cust_users.identifier')}</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('cust_appr.identifier_ph')}
                  value={inviteIdentifier}
                  onChange={e => setInviteIdentifier(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('cust_common.role')}</label>
              <Select value={inviteRole} onValueChange={v => setInviteRole(v as 'manager' | 'cashier' | 'viewer')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">{t('cust_common.role_manager')}</SelectItem>
                  <SelectItem value="cashier">{t('cust_uappr.role_cashier_seller')}</SelectItem>
                  <SelectItem value="viewer">{t('cust_common.role_viewer')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('cust_users.col_branch')}</label>
              <Select value={inviteBranch} onValueChange={setInviteBranch}>
                <SelectTrigger><SelectValue placeholder={t('cust_common.all_branches')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('cust_common.all_branches')}</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              className="w-full"
              onClick={() => inviteMutation.mutate()}
              disabled={inviteMutation.isPending || !inviteIdentifier.trim()}
            >
              {inviteMutation.isPending ? <Loader2 size={14} className="animate-spin mr-2" /> : <UserPlus size={14} className="mr-2" />}
              {t('cust_appr.send_invite')}
            </Button>
          </div>
          </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CredentialRow({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-muted/30">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-mono font-medium text-foreground break-all">{value}</p>
      </div>
      <Button type="button" size="sm" variant="outline" onClick={copy} aria-label={t('staff_invite.copy')}>
        {copied ? <CheckCircle size={14} className="text-success" /> : <Copy size={14} />}
        <span className="ml-1.5">{copied ? t('staff_invite.copied') : t('staff_invite.copy')}</span>
      </Button>
    </div>
  );
}
